import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';

import { SessionService } from '../../../auth/services/session/session.service';
import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { LoadingService } from '../../../shared/services/loading/loading.service';
import { InspectionDataResponse, InspectionPhotoResponse } from '../../models/areas.model';
import type { Event as EventType, Service } from '../../models/events.model';
import { AreasService } from '../../services/areas/areas.service';
import { EventsService } from '../../services/events/events.service';

interface Inspection {
  id: string;
  plague: string;
  plagueCategory?: string;
  infestationDegree: string;
  evidence_type: string;
  life_cycle_plague: string;
  nesting_area: string;
  zone_id?: string;
  zone?: string;
  photos: string[];
  inspection_form: string;
}

@Component({
  selector: 'app-inspections',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    DropdownModule,
    LayoutComponent,
    TableModule,
    ButtonModule,
    PaginatorModule,
  ],
  templateUrl: './inspections.component.html',
  styleUrl: './inspections.component.scss'
})
export class InspectionsComponent {
  private _areasService = inject(AreasService);
  private _eventsService = inject(EventsService);
  private _formBuilder: FormBuilder = inject(FormBuilder);
  private _loadingService = inject(LoadingService);
  private _sessionService = inject(SessionService);

  public _isModalVisible = false;
  public dataForm!: FormGroup;
  public dataFormModal!: FormGroup;
  public loadingServices = false;
  public loadingInspections = false;
  public isPhotoModalVisible = false;

  public activeService: Service | null = null;
  public services: Service[] = [];
  public inspections: Inspection[] = [];
  public selectedPhotos: string[] = [];

  ngOnInit(): void {
    this.loadDataForm();
    this.loadServices();
  }

  public get isBlocked(): boolean {
    return this._sessionService.CompanyStatus !== 'company-paid';
  }

  public get isModalVisible(): boolean {
    return this._isModalVisible;
  }

  public set isModalVisible(value: boolean) {
    this._isModalVisible = value;

    if (!value) {
      this.activeService = null;
    }
  }

  loadDataForm(): void {
    this.dataForm = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: 10,
      firstPage: 0,
      search: '',
      status__name: 'Finalizado'
    });

    this.dataFormModal = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: 10,
      firstPage: 0,
      search: '',
      id: '',
    });
  }

  loadServices(): void {
    this.loadingServices = true;

    this._eventsService.getEvents(this.dataForm.value).subscribe({
      next: (data: {
        items: EventType[];
        total: number;
        page: number;
        page_size: number;
      }) => {
        this.services = data.items.map((service) => ({
          ...service,
          employee: service.employee[0],
        }));

        this.dataForm.value.totalRecords = data.total;
        this.dataForm.value.page = data.page;
        this.dataForm.value.page_size = data.page_size;

        this.loadingServices = false;
      },
      error: (err) => {
        console.error('Error al cargar servicios:', err);
        this.loadingServices = false;
      },
    });
  }

  handleOpenModal(service: Service): void {
    if (!this.activeService) {
      this._loadingService.show();
    }

    this.activeService = service;
    this.loadingInspections = true;

    this.dataFormModal.value.id = service.id;

    this._areasService.getInspectionsFormsByEvent(this.dataFormModal.value).subscribe({
      next: (data: { items: InspectionDataResponse[], total: number, page: number, page_size: number }) => {
        this.inspections = data.items.map((inspection: any) => ({
          id: inspection.id,
          plague: inspection.plague.name,
          plagueCategory: inspection.plague.category.name,
          infestationDegree: inspection.infestation_degree.name,
          evidence_type: inspection.evidence_type.map((evidence: any) => evidence.name).join(', '),
          life_cycle_plague: inspection.life_cycle_plague.map((cycle: any) => cycle.name).join(', '),
          nesting_area: inspection.nesting_area.map((area: any) => area.name).join(', '),
          zone_id: inspection.nesting_area.length > 0 ? inspection.nesting_area[0].zone : null,
          inspection_form: inspection.inspection_form,
          photos: []
        }));

        if (this.inspections.length === 0) {
          this._loadingService.hide();
          this.loadingInspections = false;
          this._isModalVisible = true;

          return;
        }

        const photoRequests = this.inspections.map(inspection =>
          this._areasService.getInspectionPhotos(inspection.inspection_form)
        );

        forkJoin(photoRequests).subscribe({
          next: (photosResponses: InspectionPhotoResponse[][]) => {
            photosResponses.forEach((photos, index) => {
              this.inspections[index].photos = photos.map((photo) => photo.photo);
            });

            this.dataFormModal.value.totalRecords = data.total;
            this.dataFormModal.value.page = data.page;
            this.dataFormModal.value.page_size = data.page_size;

            this._loadingService.hide();
            this._isModalVisible = true;
            this.loadingInspections = false;
          },
          error: (err) => {
            console.error('Error al cargar fotos:', err);
            this.loadingInspections = false;
            this._isModalVisible = true;
          }
        });
      },
      error: (err: any) => {
        console.error('Error al cargar los datos:', err);
        this.loadingInspections = false;
      }
    });
  }

  openPhotoModal(photos: string[]): void {
    this.selectedPhotos = photos;
    this.isPhotoModalVisible = true;
  }

  pageChange(event: any) {
    const newPage = event.first / event.rows;

    this.dataForm.value.firstPage = event.first;
    this.dataForm.value.page = newPage + 1;

    this.loadServices();
  }

  pageChangeModal(event: any) {
    const newPage = event.first / event.rows;

    this.dataFormModal.value.firstPage = event.first;
    this.dataFormModal.value.page = newPage + 1;

    this.handleOpenModal(this.activeService!);
  }
}
